import { useTranslation } from '@/services/i18n'
import React, { memo, useState, useEffect, useRef } from 'react'
import styles from './index.module.scss'
import { ApiKeyItemModel } from '@/services/apis/setting/module'
import classnames from 'classnames'
import openApi from '@/services/apis/openApi'
import { checkIsPrivateKey, copyToClipboard, formatRSAPubKey } from '@/utils/setting'
import { App, Form, Input, Divider, Button } from 'antd'
import { Table } from 'antd'
import { ApiKeyModelMap, ApiOperationStateEnum, ApiOperationStateMap } from '@/constants/enum'
import ApiKeyDrawer from '../../components/ApiKeyDrawer/ApiKeyDrawer'
import ApiAccount from '@/assets/images/api-account-avatar.png'
import WebhookPrefix from '@/assets/images/webhook-prefix.png'
import Notify from '@/assets/images/notify.png'
import Delete from '@/assets/images/delete.png'
import MyTable from '@/components/Table'
import MyModal from '@/components/Modal'
import UploadRsa from '../../components/UploadRsa/UploadRsa'
import SafeheronIP from '../../components/SafeheronIP'

const { Column } = Table
const Api = memo(() => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [webhookInfo, setWebhookInfo] = useState({
    webhookUrl: '',
    webhookPubKey: '',
    webhookPlatformPubKey: '',
  })
  const [list, setList] = useState<ApiKeyItemModel[]>()
  const [currentItem, setCurrentItem] = useState<ApiKeyItemModel>({} as ApiKeyItemModel)

  async function getWebhookInfo() {
    const res = await openApi.getWebHookInfo()

    if (res.success) {
      setWebhookInfo({
        webhookUrl: res.data.webhookUrl,
        webhookPubKey: res.data.webhookPubKey,
        webhookPlatformPubKey: res.data.webhookPlatformPubKey,
      })

      setIsDisable(res.data.webhookUrl === '' ? true : false)
    }
  }
  // 只有第一次进入页面才会出现loading
  const [tableLoading, setTableLoading] = useState(true)
  async function getApiList() {
    const res = await openApi.getApiKeyList()
    setTableLoading(false)
    if (res.success) {
      setList(res.data)
    }
  }
  // 复制平台公钥
  function copyPlatformPubkey(text = webhookInfo.webhookPubKey) {
    copyToClipboard(text)
    message.success(`${t('clipboard.copySuccess')}`)
  }
  function enableUpdate(item: ApiKeyItemModel) {
    if (
      [
        ApiOperationStateEnum.SUCCESS,
        ApiOperationStateEnum.REJECT,
        ApiOperationStateEnum.DELETE_REJECT,
        ApiOperationStateEnum.UPDATE_REJECT,
      ].includes(item.operationStatus)
    ) {
      return true
    }
    return false
  }
  // 渲染表格
  function renderApiKey(record: ApiKeyItemModel) {
    return (
      <div>
        <span style={{ width: '172px', marginRight: '7px' }}> {record.apiKey}</span>
        <span className={styles.link} onClick={() => copyPlatformPubkey(record.apiKey)}>
          {t('common.copy')}
        </span>
      </div>
    )
  }

  const displayPlainText = async (userKey: string) => {
    const res: any = await openApi.showPlainText(userKey)
    if (res.success) {
      MyModal.info({
        width: 700,
        icon: null,
        title: (
          <div className={styles.Modalheader}>
            <div className={styles.img}>
              <img src={Notify} alt="" style={{ width: '24px', height: '24px' }} />
            </div>
            <div className={styles.text}> {t('api.viewPubkey')}</div>
          </div>
        ),
        content: (
          <div className={styles.publicBox}>
            <div className={styles.pubkeyItem}>
              <div className={styles.pubkeyLabel}>{t('api.name')}</div>
              <div> {res.data?.apiName}</div>
            </div>
            <div className={styles.pubkeyItem}>
              <div className={styles.pubkeyLabel}>API Key</div>
              <div> {res.data?.apiKey}</div>
            </div>
            <div className={styles.pubkeyItem}>
              <div className={styles.pubkeyLabel}>
                {t('common.platformKey')}
                <span
                  className={styles.link}
                  onClick={() => copyPlatformPubkey(res.data.platformPublicKey)}
                  style={{ marginLeft: '5px' }}
                >
                  {t('common.copy')}
                </span>
              </div>

              <textarea
                defaultValue={res.data?.platformPublicKey}
                rows={11}
                cols={90}
                disabled
                style={{ width: '600px' }}
              ></textarea>
            </div>
            <div className={styles.pubkeyItem}>
              <div className={styles.pubkeyLabel}>
                {t('api.yourPubkey')}
                <span
                  className={styles.link}
                  onClick={() => copyPlatformPubkey(res.data?.userPublicKey)}
                  style={{ marginLeft: '5px' }}
                >
                  {t('common.copy')}
                </span>
              </div>
              <textarea
                defaultValue={res.data?.userPublicKey}
                rows={11}
                cols={90}
                disabled
                style={{ width: '600px' }}
              ></textarea>
            </div>
          </div>
        ),
      })
    }
  }
  function renderPubKey(record: ApiKeyItemModel) {
    return (
      <span className={styles.link} onClick={() => displayPlainText(record?.userKey)}>
        {t('common.view')}
      </span>
    )
  }
  function renderIp(record: ApiKeyItemModel) {
    return <div>{record.ips.join(',')}</div>
  }
  function renderPermission(record: ApiKeyItemModel) {
    return record.permissionModeKeys.map((item, index) => {
      return <div key={item}>{ApiKeyModelMap[item]}</div>
    })
  }
  function renderApprove(record: ApiKeyItemModel) {
    return <div>{ApiOperationStateMap[record.operationStatus]}</div>
  }
  function renderEffect(record: ApiKeyItemModel) {
    return <div>{record.interfaceStatus === 1 ? t('policy.valid') : t('policy.invalid')}</div>
  }
  function renderSign(record: ApiKeyItemModel) {
    return (
      <div>
        {record.signmachineActivationStatus === 1 ? t('api.Activated') : t('api.Unactivated')}
      </div>
    )
  }
  function handleUpdateDialog(record: ApiKeyItemModel) {
    if (!enableUpdate(record)) return
    setCurrentItem(record)
    apikeyRef.current?.openDrawer()
  }
  // 点击操作删除出现的模态框逻辑
  function handleClose(close: any) {
    close()
  }
  async function handleConfirm(close: any, item: ApiKeyItemModel) {
    if (item) {
      if (!enableUpdate(item)) return
      const res = await openApi.deleteApiKey(item?.userKey)
      if (res.success) {
        await getApiList()
      }
      close()
    }
  }
  // 点击删除弹出modal
  function handleDelete(item: ApiKeyItemModel) {
    MyModal.confirm({
      title: (
        <div className={styles.header}>
          <div className={styles.img}>
            <img src={Delete} alt="" style={{ width: '24px', height: '24px' }} />
          </div>
          <div className={styles.text}> {t('api.delApi')}</div>
        </div>
      ),
      icon: null,
      onOk: close => handleConfirm(close, item),
      onCancel: handleClose,
      content: <div>{t('api.delApiCormfirm', { x: item?.apiName })}</div>,
    })
  }
  function renderOperation(record: ApiKeyItemModel) {
    return (
      <div>
        <span
          style={{ marginRight: '10px' }}
          className={classnames(styles.link, {
            [styles.disable]: !enableUpdate(record),
          })}
          onClick={() => handleUpdateDialog(record)}
        >
          {t('common.change')}
        </span>
        <span
          className={classnames(styles.link, {
            [styles.disable]: !enableUpdate(record),
          })}
          onClick={() => handleDelete(record)}
        >
          {t('common.delete')}
        </span>
      </div>
    )
  }
  async function handleWebhookConfirm(close: any) {
    const res = await openApi.deleteWebHook()
    if (res.success) {
      getWebhookInfo()
    }

    close()
  }
  function handleWebhookDelete() {
    MyModal.confirm({
      title: (
        <div className={styles.header}>
          <div className={styles.img}>
            <img src={Delete} alt="" style={{ width: '24px', height: '24px' }} />
          </div>
          <div className={styles.text}> {t('api.delWebhookTitle')}</div>
        </div>
      ),
      icon: null,

      onOk: handleWebhookConfirm,
      content: <div>{t('api.delWebhookContent')}</div>,
    })
  }

  useEffect(() => {
    getWebhookInfo()
    getApiList()

    const intervalId = setInterval(async () => {
      await getApiList()
    }, 10000)

    return () => clearInterval(intervalId)
  }, [])
  // modal操作
  const modalRef = useRef<any>(null)
  const [form] = Form.useForm()
  const [isDisable, setIsDisable] = useState(true)

  const urlValidator = (_: any, value: string) => {
    const urlRegex = /^(https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]$/
    if (value && !urlRegex.test(value)) {
      let message = t('rule.CountUrl')
      return Promise.reject(new Error(message))
    }
    return Promise.resolve()
  }
  function uploadSuccess(keyStr: string) {
    form.setFieldValue('pubkey', keyStr)
  }
  const handleFinish = async (value: any) => {
    const { url, pubkey } = value
    modalRef.current.update({
      okButtonProps: { loading: true },
    })
    await openApi.updateWebHook(url, pubkey)
    modalRef.current.update({
      okButtonProps: { loading: false },
    })
    // 修复没有loading
    if (closeRef.current) {
      closeRef.current()
    }
    // 不管是否成功都重置
    form.resetFields()
    getWebhookInfo()
  }
  const closeRef = useRef<Function>()
  const handleOk = (close: Function) => {
    form.submit()

    closeRef.current = close
  }
  const handleCancel = (close: Function) => {
    form.resetFields()
    close()
    setIsDisable(webhookInfo.webhookUrl === '' ? true : false)
    modalRef.current.update({
      okButtonProps: { disabled: isDisable },
    })
  }
  const pubkey = Form.useWatch('pubkey', form)
  const [timerId, setTimerId] = useState<any>(null)
  function onPubkeyChanged() {
    const isPrivKey = checkIsPrivateKey(pubkey)
    if (isPrivKey) {
      message.open({
        type: 'warning',
        content: t('api.privKeyCheck'),
      })
      form.setFieldValue('pubkey', '')
    } else {
      const formatedVal = formatRSAPubKey(pubkey)
      if (formatedVal !== pubkey) {
        modalRef.current.update({
          content: (
            <Form
              initialValues={{
                url: webhookInfo.webhookUrl,
                pubkey: webhookInfo.webhookPubKey,
              }}
              labelCol={{ span: 24 }}
              onFinish={handleFinish}
              form={form}
              onChange={handleChange}
              requiredMark={false}
            >
              <Form.Item
                name="url"
                label={t('api.callbackUrl')}
                rules={[
                  { required: true, message: t('rule.emptyUrl') as any },
                  { validator: urlValidator },
                ]}
              >
                <Input placeholder={t('api.callbackUrlPlaceholder') as string} />
              </Form.Item>

              <Form.Item
                name="pubkey"
                label={
                  <div className={styles.label}>
                    <div className={styles.text}> {t('api.RSAPublicKey')}</div>

                    <UploadRsa success={uploadSuccess}></UploadRsa>
                  </div>
                }
                tooltip={{
                  open: true,
                  title: <div className={styles.tooltip}>{t('api.pubkeyAuto')}</div>,
                }}
              >
                <Input placeholder={t('api.rsaPubkeyPlaceholder') as string} />
              </Form.Item>
            </Form>
          ),
        })
        let time = setTimeout(() => {
          modalRef.current.update({
            content: (
              <Form
                initialValues={{
                  url: webhookInfo.webhookUrl,
                  pubkey: webhookInfo.webhookPubKey,
                }}
                labelCol={{ span: 24 }}
                onFinish={handleFinish}
                form={form}
                onChange={handleChange}
                requiredMark={false}
              >
                <Form.Item
                  name="url"
                  label={t('api.callbackUrl')}
                  rules={[
                    { required: true, message: t('rule.emptyUrl') as any },
                    { validator: urlValidator },
                  ]}
                >
                  <Input placeholder={t('api.callbackUrlPlaceholder') as string} />
                </Form.Item>

                <Form.Item
                  name="pubkey"
                  label={
                    <div className={styles.label}>
                      <div className={styles.text}> {t('api.RSAPublicKey')}</div>

                      <UploadRsa success={uploadSuccess}></UploadRsa>
                    </div>
                  }
                  tooltip={{
                    open: false,
                    title: <div className={styles.tooltip}>{t('api.pubkeyAuto')}</div>,
                  }}
                >
                  <Input placeholder={t('api.rsaPubkeyPlaceholder') as string} />
                </Form.Item>
              </Form>
            ),
          })
        }, 3000)
        form.setFieldValue('pubkey', formatedVal)
        setTimerId(time)
      }
    }
  }
  useEffect(() => {
    onPubkeyChanged()

    return () => {
      clearTimeout(timerId)
    }
  }, [pubkey])

  const handleChange = () => {
    if (form.isFieldTouched('url')) {
      form
        .validateFields()
        .then(res => {
          modalRef.current.update({
            okButtonProps: { disabled: false },
          })
        })
        .catch(err => {
          setIsDisable(true)
          modalRef.current.update({
            okButtonProps: { disabled: true },
          })
        })
    }
  }

  const handleOpenUpdate = () => {
    // webhookRef.current?.openModal(webhookInfo.webhookUrl, webhookInfo.webhookPubKey)
    // WebHooksDialog.open(webhookInfo.webhookUrl, webhookInfo.webhookPubKey, getWebhookInfo)

    modalRef.current = MyModal.confirm({
      icon: null,
      title: (
        <div className={styles.header}>
          <div className={styles.img}>
            <img src={WebhookPrefix} alt="" style={{ width: '24px', height: '24px' }} />
          </div>
          <div className={styles.text}> {t('api.webookManage')}</div>
        </div>
      ),
      content: (
        <Form
          initialValues={{
            url: webhookInfo.webhookUrl,
            pubkey: webhookInfo.webhookPubKey,
          }}
          labelCol={{ span: 24 }}
          onFinish={handleFinish}
          form={form}
          onChange={handleChange}
          requiredMark={false}
        >
          <Form.Item
            name="url"
            label={t('api.callbackUrl')}
            rules={[
              { required: true, message: t('rule.emptyUrl') as any },
              { validator: urlValidator },
            ]}
          >
            <Input placeholder={t('api.callbackUrlPlaceholder') as string} />
          </Form.Item>

          <Form.Item
            name="pubkey"
            label={
              <div className={styles.label}>
                <div className={styles.text}> {t('api.RSAPublicKey')}</div>

                <UploadRsa success={uploadSuccess}></UploadRsa>
              </div>
            }
            tooltip={{ open: false, title: t('api.pubkeyAuto') }}
          >
            <Input placeholder={t('api.rsaPubkeyPlaceholder') as string} />
          </Form.Item>
        </Form>
      ),
      onOk: handleOk,
      okButtonProps: { disabled: isDisable, loading: false },
      onCancel: handleCancel,
    })
  }
  const handleOpenAdd = () => {
    modalRef.current = MyModal.confirm({
      icon: null,
      title: (
        <div className={styles.header}>
          <div className={styles.img}>
            <img src={WebhookPrefix} alt="" style={{ width: '24px', height: '24px' }} />
          </div>
          <div className={styles.text}> {t('api.webookManage')}</div>
        </div>
      ),
      content: (
        <Form
          labelCol={{ span: 24 }}
          onFinish={handleFinish}
          form={form}
          onChange={handleChange}
          requiredMark={false}
        >
          <Form.Item
            name="url"
            label={t('api.callbackUrl')}
            rules={[
              { required: true, message: t('rule.emptyUrl') as any },
              { validator: urlValidator },
            ]}
          >
            <Input placeholder={t('api.callbackUrlPlaceholder') as string} />
          </Form.Item>

          <Form.Item
            name="pubkey"
            label={
              <div className={styles.label}>
                <div className={styles.text}> {t('api.RSAPublicKey')}</div>

                <UploadRsa success={uploadSuccess}></UploadRsa>
              </div>
            }
            tooltip={{ open: false, title: t('api.pubkeyAuto') }}
          >
            <Input placeholder={t('api.rsaPubkeyPlaceholder') as string} />
          </Form.Item>
        </Form>
      ),
      onOk: handleOk,
      okButtonProps: { disabled: isDisable, loading: false },
      onCancel: handleCancel,
    })
  }

  // 抽屉操作
  const apikeyRef = useRef<any>(null)
  function handleOpenDialog() {
    setCurrentItem({} as ApiKeyItemModel)
    apikeyRef.current?.openDrawer()
  }

  function handleSafeheronIP() {
    MyModal.info({
      title: null,
      footer: null,
      icon: null,
      content: <SafeheronIP />,
      width: 350,
    })
  }
  return (
    <div className={styles.ApiBox}>
      <div className={styles.webHook}>
        <div className={styles.title}>
          <span style={{ marginRight: '25px' }}> {t('api.webookManage')}</span>
          <Button type="link" onClick={handleSafeheronIP}>
            {t('api.SafeheronIp')}
          </Button>
        </div>
        <div className={styles.change}>
          <div className={styles.hook_item}>
            <div className={styles.hook_label}>{t('api.callbackUrl')}</div>
            <div className={styles.hook_box}>
              <span
                className={classnames(styles.ellipsis, styles.hook_input)}
                title={webhookInfo.webhookUrl}
              >
                {webhookInfo.webhookUrl || t('common.none')}
              </span>
            </div>
          </div>
          <div className={styles.hook_item}>
            <div className={styles.hook_label}>{t('api.rsaPubKey')}</div>
            <div className={styles.hook_box}>
              <span
                className={classnames(styles.ellipsis, styles.hook_input)}
                title={webhookInfo.webhookPubKey}
              >
                {webhookInfo.webhookPubKey || t('common.none')}
              </span>
            </div>
          </div>

          <div className={styles.hook_item}>
            <div className={styles.hook_label}>{t('common.platformKey')}</div>
            <div className={styles.hook_box}>
              <span
                className={classnames(styles.ellipsis, styles.hook_input)}
                title={webhookInfo.webhookPlatformPubKey}
              >
                {webhookInfo.webhookPlatformPubKey}
              </span>

              <span
                className={styles.append_btn}
                onClick={() => copyPlatformPubkey(webhookInfo.webhookPlatformPubKey)}
              >
                {t('common.copy')}
              </span>
            </div>
          </div>
          {webhookInfo.webhookUrl ? (
            <div className={styles.button}>
              <span className={classnames(styles.link)} onClick={handleOpenUpdate}>
                {t('common.change')}
              </span>
              <span
                className={classnames(styles.link, styles.danger)}
                onClick={handleWebhookDelete}
              >
                {t('common.delete')}
              </span>
            </div>
          ) : (
            <div className={styles.button}>
              <span className={classnames(styles.link)} onClick={handleOpenAdd}>
                {t('common.add')}
              </span>
            </div>
          )}
        </div>
      </div>
      <Divider />
      <div className={styles.accountList}>
        <div className={styles.createBtn} onClick={() => handleOpenDialog()}>
          <img src={ApiAccount} alt="" style={{ width: '24px', marginRight: '8px' }} />
          {t('api.creatApi')}
        </div>
      </div>

      <MyTable
        dataSource={list}
        rowKey="userKey"
        pagination={{ hideOnSinglePage: true, pageSize: list?.length }}
        loading={tableLoading}
      >
        <Column title={t('api.name')} dataIndex="apiName" />
        <Column title="API Key" render={renderApiKey} />
        <Column title={t('common.pubkey')} render={renderPubKey} width={100} />
        <Column title={t('api.ipWhite')} render={renderIp} />
        <Column title={t('api.permissions')} render={renderPermission} />
        <Column title={t('api.approveState')} render={renderApprove} width={150} />
        <Column title={t('policy.effectiveState')} render={renderEffect} width={140} />
        <Column title={t('api.SigningMachine')} render={renderSign} width={140} />
        <Column title={t('common.operation')} render={renderOperation} width={160} />
      </MyTable>
      <ApiKeyDrawer ref={apikeyRef} success={getApiList} data={currentItem}></ApiKeyDrawer>
    </div>
  )
})

export default () => (
  <App>
    <Api />
  </App>
)
